import { m } from "#/paraglide/messages";
import {
  Badge,
  Button,
  createDataTableColumnHelper,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Link as UiLink,
  SearchField,
  Switch,
} from "@beweegbaas/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useMemo, useState } from "react";
import { toast } from "@beweegbaas/ui";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { Page } from "#/components/page";
import { Pager } from "#/components/pager";
import { PendingContent } from "#/components/pending-content";
import { useDownloadAllMediakaartenSociaalEmotioneelReeks } from "#/modules/mediakaarten/download-mediakaarten-reeks";
import {
  getSchoolSocialEmotionalOverview,
  getSocialEmotionalDetail,
} from "#/modules/sociaal-emotioneel/social-emotional.functions";
import { socialEmotionalOverviewQueryOptions } from "#/modules/sociaal-emotioneel.overview/query.social-emotional-overview";
import {
  SOCIAL_EMOTIONAL_OVERVIEW_SEARCH_DEFAULTS,
  socialEmotionalOverviewSearchSchema,
} from "#/modules/sociaal-emotioneel.overview/schema.search";
import { TogglePublishButton } from "#/modules/sociaal-emotioneel.overview/toggle-publish-button";
import { useOverviewSearch } from "#/modules/util.overview/use-overview-search";
import {
  getLeerjaarComboTranslation,
  getSociaalEmotioneelDomeinTranslation,
} from "#/modules/util.i18n/enum-mapping";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/social-emotional/",
)({
  validateSearch: socialEmotionalOverviewSearchSchema,
  // Keeps default filter/sort/pagination values out of the URL.
  search: { middlewares: [stripSearchParams(SOCIAL_EMOTIONAL_OVERVIEW_SEARCH_DEFAULTS)] },
  // All search keys feed the query, so they belong in the match identity — entering with
  // different filters re-runs the loader (staleTime only skips same-deps re-entries).
  loaderDeps: ({ search }) => search,
  staleTime: Infinity,
  // Primes the same query the component reads; loader data doubles as the render fallback
  // when the query cache entry was GC'd.
  loader: ({ context, params: { school, schoolyear }, deps }) =>
    context.queryClient.ensureQueryData(
      socialEmotionalOverviewQueryOptions({ school, schoolyear }, deps),
    ),
  component: SocialEmotionalPage,
  errorComponent: SocialEmotionalError,
});

function SocialEmotionalError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}

type ThemaRow = Awaited<
  ReturnType<typeof getSchoolSocialEmotionalOverview>
>["sociaalEmotioneleThemas"][number];

// Full-width domein group headers are emulated as marker rows (DataTable has no colspan rows).
type SocialEmotionalTableRow =
  | { kind: "group"; domein: ThemaRow["domein"] }
  | { kind: "thema"; thema: ThemaRow };

const helper = createDataTableColumnHelper<SocialEmotionalTableRow>();

function SocialEmotionalPage() {
  const loaderData = Route.useLoaderData();
  const { schoolyear, school } = Route.useParams();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const { includeDefaults } = search;
  const overview = useOverviewSearch(search, navigate);

  // Reads live in TanStack Query (same pattern as the activities overview) so the publish-toggle's
  // invalidation refreshes this list; `loaderData` is only the render fallback after cache GC.
  const overviewQuery = useQuery({
    ...socialEmotionalOverviewQueryOptions({ school, schoolyear }, search),
    placeholderData: keepPreviousData,
  });
  const { sociaalEmotioneleThemas, totalPages, totalCount } = overviewQuery.data ?? loaderData;
  const [downloadingMediakaartenReeksId, setDownloadingMediakaartenReeksId] = useState<
    string | null
  >(null);
  const downloadAllMediakaartenMutation = useDownloadAllMediakaartenSociaalEmotioneelReeks();
  const getDetailFn = useServerFn(getSocialEmotionalDetail);
  const isNavigationPending = useRouterState({ select: (s) => s.status === "pending" });
  // Search snapshot the CURRENTLY RENDERED rows were loaded with — used to gate the "no custom
  // themes" empty message so it only shows for a settled, genuinely-unsearched, empty list. Reading
  // the live `Route.useSearch()` value here would flip the message on/off against stale data during
  // in-flight revalidation (search updates instantly on navigate; loader data lags until commit).
  const appliedSearchTerm = Route.useLoaderDeps({ select: (deps) => deps.searchTerm });

  const handleDownloadMediakaarten = async (rowId: string) => {
    setDownloadingMediakaartenReeksId(rowId);
    try {
      const data = await getDetailFn({ data: { id: rowId } });
      if (!data) {
        setDownloadingMediakaartenReeksId(null);
        return;
      }
      downloadAllMediakaartenMutation.mutate(
        { reeks: data.sociaalEmotioneelThemasReeks },
        { onSettled: () => setDownloadingMediakaartenReeksId(null) },
      );
    } catch {
      toast.error(m["mediakaarten.download_failed"]());
      setDownloadingMediakaartenReeksId(null);
    }
  };

  const rows = useMemo<SocialEmotionalTableRow[]>(() => {
    const groups = new Map<ThemaRow["domein"], ThemaRow[]>();
    for (const theme of sociaalEmotioneleThemas) {
      const group = groups.get(theme.domein) ?? [];
      group.push(theme);
      groups.set(theme.domein, group);
    }
    return [...groups.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .flatMap(([domein, themes]) => [
        { kind: "group" as const, domein },
        ...themes.map((thema) => ({ kind: "thema" as const, thema })),
      ]);
  }, [sociaalEmotioneleThemas]);

  const columns = useMemo(
    () =>
      helper.columns([
        helper.display({
          id: "name",
          header: () => m["common.fields.name"](),
          enableSorting: true,
          meta: { rowTarget: true },
          cell: ({ row }) => {
            if (row.original.kind === "group")
              return getSociaalEmotioneelDomeinTranslation(row.original.domein);
            const thema = row.original.thema;
            return (
              <UiLink
                variant="secondary"
                underline
                to="/$locale/{-$schoolyear}/$school/social-emotional/$id/edit"
                params={(prev: Record<string, string>) => ({ ...prev, id: thema.id })}
              >
                {thema.name}
              </UiLink>
            );
          },
        }),
        helper.display({
          id: "domein",
          header: () => m["sociaal_emotioneel_thema.overview.table.domein"](),
          cell: ({ row }) =>
            row.original.kind === "thema"
              ? getSociaalEmotioneelDomeinTranslation(row.original.thema.domein)
              : null,
        }),
        helper.display({
          id: "classGradeGrouping",
          header: () => m["common.leerjaar"](),
          cell: ({ row }) =>
            row.original.kind === "thema" ? (
              <div className="flex flex-wrap gap-2">
                {row.original.thema.leerjaarCombo
                  .map((leerjaarCombo) => getLeerjaarComboTranslation(leerjaarCombo))
                  .join(", ")}
              </div>
            ) : null,
        }),
        helper.display({
          id: "isDefault",
          header: "",
          meta: { cellClassName: "w-0" },
          cell: ({ row }) =>
            row.original.kind === "thema" ? (
              row.original.thema.isDefault ? (
                <Badge variant="gray" size="sm">
                  {m["common.app_name"]()}
                </Badge>
              ) : (
                <Badge size="sm">{m["common.labels.eigen_thema"]()}</Badge>
              )
            ) : null,
        }),
        helper.display({
          id: "actions",
          header: "",
          meta: { action: true, headerClassName: "w-0" },
          cell: ({ row }) => {
            if (row.original.kind !== "thema") return null;
            const thema = row.original.thema;
            return (
              <>
                {thema.canTogglePublished && (
                  <TogglePublishButton reeksId={thema.id} isPublished={thema.isPublished} />
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={<Button variant="secondary" size="icon" aria-label="Acties" />}
                  >
                    <IconMdiDotsVertical className="size-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      render={
                        <Link
                          from={Route.fullPath}
                          to="/$locale/{-$schoolyear}/$school/social-emotional/mediakaart-preview/$reeksId"
                          params={(prev: Record<string, string>) => ({
                            ...prev,
                            reeksId: thema.id,
                          })}
                        />
                      }
                      nativeButton={false}
                    >
                      <IconMaterialSymbolsVisibilityOutline className="size-4" />
                      Preview mediakaarten
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={downloadingMediakaartenReeksId === thema.id}
                      onClick={(e) => {
                        e.preventDefault();
                        void handleDownloadMediakaarten(thema.id);
                      }}
                    >
                      <IconMaterialSymbolsDownload className="size-4" />
                      Download mediakaarten
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            );
          },
        }),
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadingMediakaartenReeksId],
  );

  return (
    <Page>
      <Page.Header>
        <Page.Header.SearchField>
          <SearchField
            className={"w-auto grow md:w-xs"}
            placeholder={m["common.placeholders.search_by_name"]()}
            {...overview.searchFieldProps}
          />
        </Page.Header.SearchField>

        <Page.Header.FilterItems className="hidden items-center gap-2 md:flex">
          <Switch
            id="showBeweegbaasThemes"
            name={"showBeweegbaasThemes"}
            checked={includeDefaults}
            onCheckedChange={(checked) => {
              void navigate({
                search: (prev) => ({ ...prev, includeDefaults: checked, page: 1 }),
                replace: true,
                resetScroll: false,
              });
            }}
          />
          <label htmlFor="showBeweegbaasThemes" className="text-sm">
            {m["sociaal_emotioneel_thema.overview.show_beweegbaas_themes"]()}
          </label>
        </Page.Header.FilterItems>
        <Page.Header.Actions>
          <Page.Header.MainAction>
            <Button
              className={"max-sm:w-full sm:ml-auto"}
              size="xl"
              render={
                <Link
                  from={Route.fullPath}
                  to="/$locale/{-$schoolyear}/$school/social-emotional/add"
                  params={(prev: Record<string, string>) => prev}
                />
              }
              nativeButton={false}
            >
              {m["common.actions.add"]()}
              <IconMaterialSymbolsAdd />
            </Button>
          </Page.Header.MainAction>
        </Page.Header.Actions>
      </Page.Header>
      <PendingContent>
        <DataTable
          alignToContainer
          aria-label="Sociaal-emotionele thema's"
          columns={columns}
          data={rows}
          getRowId={(row) => (row.kind === "group" ? `group-${row.domein}` : row.thema.id)}
          empty={
            !appliedSearchTerm && !isNavigationPending
              ? m["sociaal_emotioneel_thema.overview.no_custom_themes"]()
              : undefined
          }
          getRowClassName={(row) =>
            row.kind === "group"
              ? "bg-muted hover:bg-muted font-bold"
              : !row.thema.isPublished
                ? "text-muted-foreground line-through"
                : undefined
          }
          {...overview.sortProps}
        />

        <Page.Container>
          <Pager
            totalPages={totalPages}
            totalCount={totalCount}
            className={"border-0"}
            {...overview.pagerProps}
          />
        </Page.Container>
      </PendingContent>
    </Page>
  );
}
